import React, { useState, useEffect } from 'react';
import { Users, Briefcase, Fuel, Cog } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { VITO } from '../data/fleet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Fleet = ({ t, onBookCar, language }) => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cars/available`);
        if (response.ok) {
          const data = await response.json();
          data.sort((a, b) => a.pricePerDay - b.pricePerDay);
          setCars(data);
        } else {
          setError('Failed to load fleet');
        }
      } catch (error) {
        console.error('Failed to fetch cars:', error);
        setError('Failed to connect to server');
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, []);

  if (loading) {
    return (
      <section id="fleet" className="py-24 sm:py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#10B981] border-t-transparent mb-4"></div>
            <p className="text-gray-500">Loading fleet...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || cars.length === 0) {
    return (
      <section id="fleet" className="py-24 sm:py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center py-12">
            <p className="text-gray-500">Unable to load fleet. Please refresh the page.</p>
          </div>
        </div>
      </section>
    );
  }

  const displayCars = cars.map((c) => (c.id === VITO.id ? { ...c, ...VITO.card } : c));
  const vitoHref = language === 'ro' ? VITO.slugRo : VITO.slugEn;
  const vitoLabel = language === 'ro' ? 'Detalii, galerie și condiții minivan →' : 'Minivan details, gallery & conditions →';

  return (
    <section id="fleet" className="py-20 sm:py-28 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section Header */}
        <div className="mb-12 sm:mb-16">
          <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
            {language === 'ro' ? 'Colecția Noastră' : 'Our Collection'}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0D1B3E]">
            {t.fleet.title}
          </h2>
        </div>

        {/* Cars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {displayCars.map((car) => (
            <div 
              key={car.id} 
              data-testid={`fleet-car-${car.id}`}
              className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-xl hover:border-[#10B981]/30 transition-all duration-300"
            >
              {/* Best Value Badge */}
              {car.id === 3 && (
                <div className="absolute top-4 left-4 z-10 bg-[#10B981] text-white px-3 py-1 rounded text-xs font-bold uppercase tracking-wider">
                  Best Value
                </div>
              )}
              
              {/* Image Container */}
              <div className="relative h-52 sm:h-56 overflow-hidden bg-gray-100">
                <img
                  src={car.image}
                  alt={car.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Price Tag */}
                <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-lg shadow-md">
                  <span className="text-[#0D1B3E] font-bold text-lg">€{car.pricePerDay}</span>
                  <span className="text-gray-500 text-sm ml-1">/{language === 'ro' ? 'zi' : 'day'}</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 sm:p-6">
                <p className="text-[#10B981] text-xs font-semibold tracking-wider uppercase mb-2">{car.category}</p>
                <h3 className="font-display text-xl font-bold text-[#0D1B3E] mb-4">{car.name}</h3>

                {/* Specs Grid */}
                <div className="grid grid-cols-2 gap-3 mb-5 text-gray-600 text-sm">
                  <div className="flex items-center gap-2">
                    <Users size={16} className="text-[#10B981]" />
                    <span>{car.passengers} {language === 'ro' ? 'pasageri' : 'passengers'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Briefcase size={16} className="text-[#10B981]" />
                    <span>{car.luggage} {language === 'ro' ? 'bagaje' : 'luggage'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Cog size={16} className="text-[#10B981]" />
                    <span>{car.transmission}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Fuel size={16} className="text-[#10B981]" />
                    <span>{car.fuel}</span>
                  </div>
                </div>

                {/* CTA Button */}
                <Button
                  onClick={() => onBookCar(car)}
                  data-testid={`book-car-${car.id}`}
                  className="w-full bg-[#0D1B3E] hover:bg-[#1A3A6B] text-white font-semibold py-4 rounded transition-all"
                >
                  {t.fleet.bookNow}
                </Button>
                {car.id === VITO.id && (
                  <Link
                    to={vitoHref}
                    data-testid="fleet-vito-details-link"
                    className="mt-3 inline-flex items-center justify-center w-full text-sm font-semibold text-[#10B981] hover:underline"
                  >
                    {vitoLabel}
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Fleet;
